import { gql } from "@apollo/client";
import { FRAGMENT_BASE_RESPONSE_FIELDS } from "api/fragments";

export const UPDATE_BROKER_DETAILS = gql`
  mutation updateStep(
    $applicationId: UUID
    $data: ApplicationBrokerDetailInput!
    $case: UUID
    $validated: Boolean!
  ) {
    applications {
      data: updateBrokerDetail(
        applicationId: $applicationId
        data: $data
        case: $case
        validated: $validated
      ) {
        ... on ApplicationBrokerDetail {
          email
          application {
            uuid
            reference
            modifiedOn
            case {
              reference
              uuid
            }
          }
          feeAmount
          brokerFeeAddedToLoan
          lenderFeeAddedToLoan
          firmName
          name
          phoneNumber
          status
        }
      }
    }
  }
`;

export const UPDATE_APPLICANT_DETAILS = gql`
  mutation updateStep(
    $applicationId: UUID
    $data: [ApplicationApplicantDetailInput!]!
    $case: UUID
    $validated: Boolean!
    $numberOfApplicants: Int
  ) {
    applications {
      data: updateApplicantDetails(
        applicationId: $applicationId
        data: $data
        case: $case
        validated: $validated
        numberOfApplicants: $numberOfApplicants
      ) {
        ... on ApplicationApplicantDetail {
          application {
            uuid
            numberOfApplicants
            reference
            modifiedOn
            case {
              reference
              uuid
            }
          }
          applicants {
            uuid
            currentAddress {
              address
              addressLine1
              addressLine2
              addressLine3
              buildingName
              buildingNumber
              county
              postcode
              residentialStatus
              stayDurationMonths
              stayDurationYears
              subBuildingName
              subBuildingNumber
              thoroughfare
              townOrCity
            }
            dateOfChange
            dob
            email
            title
            surname
            status
            previousName
            previousAddresses {
              uuid
              townOrCity
              thoroughfare
              subBuildingNumber
              subBuildingName
              stayDurationYears
              stayDurationMonths
              residentialStatus
              postcode
              county
              buildingNumber
              buildingName
              addressLine3
              addressLine2
              addressLine1
              address
            }
            phoneNumber
            nationality
            maritalStatus
            middleName
            hasPermanentUkResideRight
            forename
          }
        }
      }
    }
  }
`;

export const UPDATE_APPLICANT_EMPLOYMENT = gql`
  mutation updateStep(
    $applicationId: UUID!
    $data: [ApplicationApplicantEmploymentInput!]!
    $case: UUID
    $validated: Boolean!
  ) {
    applications {
      data: updateApplicantEmployment(
        applicationId: $applicationId
        data: $data
        case: $case
        validated: $validated
      ) {
        ... on ApplicationApplicantEmployment {
          applicantId
          status
          numberOfJobs
          incomeChangesDetails
          hasOtherSourcesOfIncome
          awareOfAnyChangesToIncome
          anticipatedRetirementAge
          employmentDetail {
            annualBasicSalary
            businessName
            currentYearProjectedDividends
            dailyRate
            dividendFromLatestFinalisedYear
            dividendFromPreviousYear
            employerName
            employmentType
            endDate
            uuid
            latestYearNetProfit
            occupation
            percentBusinessOwned
            previousYearNetProfit
            projectedGrossProfitCurrentTradingYear
            salaryFromLatestFinalisedYear
            salaryFromPreviousYear
            selfEmployedAccountant {
              townOrCity
              thoroughfare
              subBuildingNumber
              subBuildingName
              postcode
              phoneNumber
              firmName
              emailAddress
              county
              buildingNumber
              buildingName
              addressLine3
              addressLine2
              addressLine1
              address
            }
            shareholdingPercentInCompany
            yearsAtEmployer
            monthsAtEmployer
            employmentStatus
            treatedAsEmployedForTax
          }
          employmentOtherIncomeSources {
            annualGrossIncome
            uuid
            source
          }
        }
      }
    }
  }
`;

export const UPDATE_EXPENDITURE = gql`
  mutation updateStep(
    $applicationId: UUID
    $data: ApplicationExpenditureInput!
    $case: UUID
    $validated: Boolean!
  ) {
    applications {
      data: updateExpenditure(
        applicationId: $applicationId
        data: $data
        case: $case
        validated: $validated
      ) {
        ... on ApplicationExpenditure {
          application {
            uuid
            reference
            modifiedOn
            case {
              reference
              uuid
            }
          }
          status
          householdExpenditure {
            futureChangeInExpenditure
            changeInExpenditureDetail
            foodDrinkGroceries
            clothingFootwearPersonal
            utilitiesOtherHousing
            furnishingsHouseholdMaintenance
            transportTravel
            communication
            recreationRestaurantsHotels
            insurance
            councilTax
            cigarettesTobacco
            childcareSchoolFees
            adultCare
            maintenanceCsa
            mortgageEndowment
            health
            other
          }
          applicantCredit {
            applicantId
            applicantHasOutstandingCredit
            creditProviders {
              balanceOutstanding
              uuid
              monthlyPayment
              provider
              providerType
              settlementAmount
              consolidate
              isPartConsolidated
            }
          }
        }
      }
    }
  }
`;

export const UPDATE_LOAN_DETAILS = gql`
  mutation updateStep(
    $applicationId: UUID
    $data: ApplicationLoanInput!
    $case: UUID
    $validated: Boolean!
  ) {
    applications {
      data: updateLoan(
        applicationId: $applicationId
        data: $data
        case: $case
        validated: $validated
      ) {
        ... on ApplicationLoan {
          application {
            uuid
            reference
            modifiedOn
            case {
              reference
              uuid
            }
          }
          loanAmount
          firstChargeLender
          mortgageAgeYears
          mortgageAgeMonths
          firstChargeMortgageValue
          isInterestRateFixedForAnInitialPeriod
          propertyPurchasePrice
          estimatedPropertyValue
          purposeOfLoan
          loanTermMonths
          status
          remainingTermOfFirstChargeLoanYears
          remainingTermOfFirstChargeLoanMonths
          remainingTermOfInitialInterestRatePeriodMonths
        }
      }
    }
  }
`;

export const UPDATE_PROPERTY_DETAILS = gql`
  mutation updateStep(
    $applicationId: UUID
    $data: ApplicationPropertyInput!
    $case: UUID
    $validated: Boolean!
  ) {
    applications {
      data: updateProperty(
        applicationId: $applicationId
        data: $data
        case: $case
        validated: $validated
      ) {
        application {
          uuid
          reference
          modifiedOn
          case {
            reference
            uuid
          }
        }
        property {
          isSecuredPropertySameAsApplicantAddress
          townOrCity
          thoroughfare
          subBuildingNumber
          subBuildingName
          status
          propertyType
          propertyStyle
          postcode
          noOfBedrooms
          groundRentIfFlat
          county
          buildingNumber
          buildingName
          addressLine3
          addressLine2
          addressLine1
          address
          avmValuation
          avmConfidenceLevel
        }
        loan {
          loanAmount
          firstChargeLender
          mortgageAgeYears
          mortgageAgeMonths
          firstChargeMortgageValue
          isInterestRateFixedForAnInitialPeriod
          propertyPurchasePrice
          estimatedPropertyValue
          purposeOfLoan
          loanTermMonths
          status
          remainingTermOfFirstChargeLoanYears
          remainingTermOfFirstChargeLoanMonths
          remainingTermOfInitialInterestRatePeriodMonths
        }
      }
    }
  }
`;

export const UPDATE_OTHER_DETAILS = gql`
  mutation updateStep(
    $applicationId: UUID
    $data: ApplicationOtherDetailsInput!
    $case: UUID
    $validated: Boolean!
  ) {
    applications {
      data: updateOtherDetails(
        applicationId: $applicationId
        data: $data
        case: $case
        validated: $validated
      ) {
        ... on ApplicationOtherDetails {
          application {
            uuid
            reference
            modifiedOn
            case {
              reference
              uuid
            }
          }
          numberOfFinancialDependentsOver17
          numberOfFinancialDependents17Under
          status
        }
      }
    }
  }
`;

export const UPDATE_PRODUCT = gql`
  mutation updateStep(
    $applicationId: UUID
    $data: ApplicationProductInput!
    $case: UUID
    $validated: Boolean!
  ) {
    applications {
      data: updateProduct(
        applicationId: $applicationId
        data: $data
        case: $case
        validated: $validated
      ) {
        ... on ApplicationProduct {
          application {
            uuid
            reference
            modifiedOn
            case {
              reference
              uuid
            }
          }
          selectedProductUuid
          status
        }
      }
    }
  }
`;

export const UPDATE_REVIEW_CONFIRM = gql`
  mutation updateStep(
    $applicationId: UUID!
    $data: ApplicationReviewInput!
    $case: UUID
    $validated: Boolean!
  ) {
    applications {
      data: updateReview(
        applicationId: $applicationId
        data: $data
        case: $case
        validated: $validated
      ) {
        ... on ApplicationReview {
          application {
            uuid
            reference
            modifiedOn
            case {
              reference
              uuid
            }
          }
          status
        }
      }
    }
  }
`;

export const SUBMIT_APPLICATION = gql`
  mutation submitApplication(
    $applicationId: UUID!
    $case: UUID
    $validated: Boolean
  ) {
    applications {
      response: submitApplication(
        applicationId: $applicationId
        case: $case
        validated: $validated
      ) {
        ...BaseResponseFields
        application {
          uuid
          status
          case {
            reference
            uuid
          }
        }
      }
    }
  }
  ${FRAGMENT_BASE_RESPONSE_FIELDS}
`;

export const MUTATION_DUPLICATE_APPLICATION = gql`
  mutation DuplicateApplication($applicationId: String!) {
    data: applications {
      application: duplicateApplication(applicationId: $applicationId) {
        uuid
      }
    }
  }
`;

export const MUTATION_UPDATE_DEBT_CONSOLIDATION = gql`
  mutation UpdateDebtConsolidation(
    $applicationUuid: UUID!
    $data: ApplicationDebtConsolidationInput!
  ) {
    applications {
      data: updateDebtConsolidation(
        applicationUuid: $applicationUuid
        data: $data
      ) {
        ... on DebtConsolidation {
          uuid
          loanAmount
          cashOutApplicant {
            uuid
            forename
            surname
          }
          cashOutAccountNumber
          cashOutSortCode
          consolidatedAmount
          cashOutAmount
          cashOutPurpose
          creditors {
            uuid
            applicant {
              uuid
              forename
              surname
            }
            providerType
            provider
            sortCode
            accountNumber
            settlementAmount
            isPartConsolidated
            reference
            paidTo
            purpose
          }
        }
      }
    }
  }
`;
